import { useState, useEffect, useRef, useMemo } from "react";
import { SparklesIcon, CheckCircleIcon, ChevronDownIcon, ChevronRightIcon } from "../../../automationStudioIcons";
import useAutomationTextFormatter from "../../../../AutomationStudio/hooks/useAutomationTextFormatter";
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";

/**
 * Instruction status during AI generation:
 *  - "neutral"     → not yet reached (no border)
 *  - "inprogress"  → currently receiving steps (blue border + pulse)
 *  - "skipped"     → was in-progress but got 0 steps and moved on (grey border)
 *  - "completed"   → received at least one step and is no longer in-progress
 */

/**
 * GeneratingView - Instruction-aware live generation view
 *
 * Instead of a generic spinner, this shows every instruction card appearing
 * with an entrance animation. As AI steps stream in via WebSocket they are
 * grouped under their parent instruction. The instruction that is currently
 * receiving steps gets a blue "in-progress" border highlight.
 */
export default function GeneratingView({
    instructions = [],
    isGenerating = false,
}) {
    const { format } = useAutomationTextFormatter();

    // Track which instructions have appeared (staggered entrance animation)
    const [visibleCount, setVisibleCount] = useState(0);
    const animTimerRef = useRef(null);

    // Track instruction statuses: { [instructionId]: "neutral" | "inprogress" | "skipped" | "completed" }
    const [instructionStatuses, setInstructionStatuses] = useState({});
    // Track which instruction was previously in-progress so we can decide skipped/completed
    const prevInProgressIdRef = useRef(null);
    // Toggle per-instruction expand
    const [expandedMap, setExpandedMap] = useState({});

    // Only consider instructions that have text content (empty slots are ignored)
    const validInstructions = useMemo(
        () => instructions.filter((inst) => inst.text?.trim()),
        [instructions]
    );

    // ------------------------------------------------------------------
    // Staggered entrance: reveal one instruction card every 300ms
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!isGenerating || validInstructions.length === 0) return;

        // Reset
        setVisibleCount(0);
        setInstructionStatuses({});
        prevInProgressIdRef.current = null;

        // Expand all by default
        const expMap = {};
        validInstructions.forEach((inst) => {
            expMap[inst.id] = true;
        });
        setExpandedMap(expMap);

        let count = 0;
        animTimerRef.current = setInterval(() => {
            count += 1;
            setVisibleCount(count);

            // First instruction starts as inprogress
            if (count === 1) {
                const firstId = validInstructions[0]?.id;
                if (firstId != null) {
                    setInstructionStatuses((prev) => ({
                        ...prev,
                        [firstId]: "inprogress",
                    }));
                    prevInProgressIdRef.current = firstId;
                }
            }

            if (count >= validInstructions.length) {
                clearInterval(animTimerRef.current);
            }
        }, 300);

        return () => clearInterval(animTimerRef.current);
    }, [isGenerating, validInstructions.length]);

    // ------------------------------------------------------------------
    // Detect which instruction is receiving steps → update statuses
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!isGenerating) return;

        // Find the instruction that currently has the latest new step
        // We look at which instructionIds have aiSteps and pick the latest one
        // with steps that is different from the last known in-progress instruction
        let latestActiveId = null;

        for (let i = validInstructions.length - 1; i >= 0; i--) {
            const inst = validInstructions[i];
            if (inst.aiSteps && inst.aiSteps.length > 0) {
                latestActiveId = inst.id;
                break;
            }
        }

        if (latestActiveId == null) return; // no steps yet

        const prevId = prevInProgressIdRef.current;

        // If the active instruction changed, transition the old one
        if (prevId != null && prevId !== latestActiveId) {
            setInstructionStatuses((prev) => {
                const next = { ...prev };

                // Decide the status of the old in-progress instruction
                const oldInst = validInstructions.find((inst) => inst.id === prevId);
                const oldHasSteps = oldInst?.aiSteps && oldInst.aiSteps.length > 0;
                next[prevId] = oldHasSteps ? "completed" : "skipped";

                // Mark all instructions between oldId and newId that were not touched as skipped
                const oldIdx = validInstructions.findIndex((inst) => inst.id === prevId);
                const newIdx = validInstructions.findIndex((inst) => inst.id === latestActiveId);
                if (oldIdx >= 0 && newIdx >= 0) {
                    for (let i = oldIdx + 1; i < newIdx; i++) {
                        const midInst = validInstructions[i];
                        if (!next[midInst.id] || next[midInst.id] === "inprogress" || next[midInst.id] === "neutral") {
                            const midHasSteps = midInst.aiSteps && midInst.aiSteps.length > 0;
                            next[midInst.id] = midHasSteps ? "completed" : "skipped";
                        }
                    }
                }

                // New instruction is now in-progress
                next[latestActiveId] = "inprogress";
                return next;
            });
            prevInProgressIdRef.current = latestActiveId;
        } else if (prevId === latestActiveId) {
            // Same instruction still receiving steps — keep it inprogress
            setInstructionStatuses((prev) => {
                if (prev[latestActiveId] === "inprogress") return prev;
                return { ...prev, [latestActiveId]: "inprogress" };
            });
        } else {
            // First time any steps arrive
            setInstructionStatuses((prev) => ({
                ...prev,
                [latestActiveId]: "inprogress",
            }));
            prevInProgressIdRef.current = latestActiveId;
        }
    }, [isGenerating, validInstructions]);

    // ------------------------------------------------------------------
    // When generation is complete mark any remaining inprogress as completed/skipped
    // ------------------------------------------------------------------
    useEffect(() => {
        if (isGenerating) return;
        setInstructionStatuses((prev) => {
            let changed = false;
            const next = { ...prev };
            validInstructions.forEach((inst) => {
                if (next[inst.id] === "inprogress" || next[inst.id] === "neutral" || !next[inst.id]) {
                    const hasSteps = inst.aiSteps && inst.aiSteps.length > 0;
                    next[inst.id] = hasSteps ? "completed" : "skipped";
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [isGenerating, validInstructions]);

    // ------------------------------------------------------------------
    // Toggle expand
    // ------------------------------------------------------------------
    const toggleExpand = (id) => {
        setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // Auto-scroll the list so the latest instruction is visible
    const listRef = useRef(null);
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [visibleCount, validInstructions]);

    // ------------------------------------------------------------------
    // Get CSS class for instruction status border
    // ------------------------------------------------------------------
    const getStatusClass = (instId) => {
        const status = instructionStatuses[instId];
        switch (status) {
            case "inprogress":
                return styles.genInstructionInprogress;
            case "skipped":
                return styles.genInstructionSkipped;
            case "completed":
                return styles.genInstructionCompleted;
            default:
                return styles.genInstructionNeutral;
        }
    };

    const getStatusLabel = (instId) => {
        const status = instructionStatuses[instId];
        switch (status) {
            case "inprogress":
                return "In Progress";
            case "skipped":
                return "Skipped";
            case "completed":
                return "Completed";
            default:
                return "";
        }
    };

    // If no valid instructions yet, show the default generating animation
    if (validInstructions.length === 0) {
        return (
            <div className={styles.generatingViewContainer}>
                <div className={styles.generatingContent}>
                    <div className={styles.generatingIconWrapper}>
                        <span className={styles.generatingSpinner} aria-hidden role="presentation" />
                        <SparklesIcon className={styles.generatingIcon} />
                    </div>
                    <h2 className={`${styles.generatingTitle} ${styles.asTypoSubtitle2}`}>
                        Generating AI Steps...
                    </h2>
                    <p className={`${styles.generatingSubtitle} ${styles.asTypoBody2}`}>
                        Our AI is analyzing your instructions and creating optimized test steps
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.genLiveContainer}>
            {/* Header */}
            <div className={styles.genLiveHeader}>
                <div className={styles.genLiveHeaderLeft}>
                    <span
                        className={`${styles.asSpinner} ${styles.asSpinnerSm}`}
                        style={{ borderTopColor: "#2563EB" }}
                        aria-hidden
                        role="presentation"
                    />
                    <h2 className={`${styles.genLiveTitle} ${styles.asTypoSubtitle2}`}>
                        Generating AI Steps...
                    </h2>
                </div>
                <span className={`${styles.genLiveCount} ${styles.asTypoCaption}`}>
                    {validInstructions.reduce((sum, inst) => sum + (inst.aiSteps?.length || 0), 0)} steps generated
                </span>
            </div>

            {/* Instruction cards with steps */}
            <div className={styles.genLiveList} ref={listRef}>
                {validInstructions.map((instruction, idx) => {
                    if (idx >= visibleCount) return null;
                    const isExpanded = expandedMap[instruction.id] !== false;
                    const steps = instruction.aiSteps || [];
                    const statusClass = getStatusClass(instruction.id);
                    const statusLabel = getStatusLabel(instruction.id);
                    const status = instructionStatuses[instruction.id];
                    const instructionDisplayText = format(instruction.text ?? "");

                    return (
                        <div
                            key={instruction.id}
                            className={`${styles.genInstructionCard} ${statusClass}`}
                            style={{ animationDelay: `${idx * 0.05}s` }}
                        >
                            {/* Instruction header */}
                            <div
                                className={styles.genInstructionHeader}
                                onClick={() => toggleExpand(instruction.id)}
                            >
                                <div className={styles.genInstructionHeaderLeft}>
                                    <div className={styles.genInstructionNumber}>{idx + 1}</div>
                                    <div className={styles.genInstructionTextWrap}>
                                        <span
                                            className={`${styles.tooltipHost} ${styles.genInstructionText} ${styles.asTypoBody2}`}
                                            title={instructionDisplayText}
                                        >
                                            {instructionDisplayText}
                                        </span>
                                        <span className={`${styles.genInstructionMeta} ${styles.asTypoCaption}`}>
                                            {steps.length > 0
                                                ? `${steps.length} step${steps.length !== 1 ? "s" : ""}`
                                                : status === "inprogress"
                                                    ? "Generating..."
                                                    : status === "skipped"
                                                        ? "No steps"
                                                        : "Waiting..."}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.genInstructionHeaderRight}>
                                    {statusLabel && (
                                        <span
                                            className={`${styles.genStatusBadge} ${status === "inprogress"
                                                    ? styles.genStatusBadgeActive
                                                    : status === "skipped"
                                                        ? styles.genStatusBadgeSkipped
                                                        : status === "completed"
                                                            ? styles.genStatusBadgeCompleted
                                                            : ""
                                                } ${styles.asTypoCaption}`}
                                        >
                                            {status === "inprogress" && (
                                                <span
                                                    className={`${styles.asSpinner}`}
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderWidth: 1.5,
                                                        borderTopColor: "#2563EB",
                                                    }}
                                                    aria-hidden
                                                    role="presentation"
                                                />
                                            )}
                                            {statusLabel}
                                        </span>
                                    )}
                                    {steps.length > 0 && (
                                        isExpanded ? (
                                            <ChevronDownIcon className={styles.genChevron} />
                                        ) : (
                                            <ChevronRightIcon className={styles.genChevron} />
                                        )
                                    )}
                                </div>
                            </div>

                            {/* AI steps list */}
                            {isExpanded && steps.length > 0 && (
                                <div className={styles.genStepsList}>
                                    {steps.map((step, stepIdx) => {
                                        const stepDescDisplay = format(
                                            step.description || step.tool || "Step"
                                        );
                                        return (
                                        <div
                                            key={step.id || `step-${stepIdx}`}
                                            className={styles.genStepRow}
                                            style={{ animationDelay: `${stepIdx * 0.08}s` }}
                                        >
                                            <div className={styles.genStepDot} />
                                            <div className={styles.genStepContent}>
                                                <span className={`${styles.genStepDesc} ${styles.asTypoBody2}`}>
                                                    {stepDescDisplay}
                                                </span>
                                                {step.tool && (
                                                    <span className={`${styles.genStepTool} ${styles.asTypoCaption}`}>
                                                        {step.tool}
                                                    </span>
                                                )}
                                            </div>
                                            {step.status === "success" && (
                                                <CheckCircleIcon
                                                    className={styles.genStepStatusIcon}
                                                    style={{ color: "#16A34A" }}
                                                />
                                            )}
                                        </div>
                                    );
                                    })}
                                </div>
                            )}

                            {/* Inline loader for in-progress instruction without steps yet */}
                            {status === "inprogress" && steps.length === 0 && (
                                <div className={styles.genInstructionLoader}>
                                    <div className={styles.genPulseBar} />
                                    <div className={styles.genPulseBar} style={{ width: "60%", animationDelay: "0.15s" }} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
